import React from "react";

import { createPortal } from "react-dom";

import { useTranslation } from "react-i18next";

import { useAuth0 } from "@auth0/auth0-react";

import { AlertTriangle, Loader2, Unplug, X } from "lucide-react";

import { useToast } from "../../../components/feedback/ToastProvider";

import "./DashboardExternalRenovationCard.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const COUNTRY_NAMES = {
  BE: "Belgium",
  ES: "Spain",
  GR: "Greece",
  LU: "Luxembourg",
  PT: "Portugal",
};

function formatCurrency(value, countryCode) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  const currency = ["BE", "ES", "GR", "LU", "PT"].includes(
    String(countryCode || "").toUpperCase(),
  )
    ? "EUR"
    : "EUR";

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(numericValue);
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export default function DashboardExternalRenovationCard({
  summary = {},
  onCreateDossier,
  onLinkDossier,
  onManage,
  onRefresh,
}) {
  const { t } = useTranslation("homebuyer-dashboard");

  const { getAccessTokenSilently } = useAuth0();

  const { showToast } = useToast();

  const [disconnectModalOpen, setDisconnectModalOpen] = React.useState(false);

  const [disconnecting, setDisconnecting] = React.useState(false);

  /* =========================================================
     STATE
     ========================================================= */

  const hasDossier = Boolean(summary?.dossier_id);

  const isLinked = Boolean(summary?.external_renovation_is_linked);

  const hasPlan = Boolean(summary?.external_renovation_has_plan);

  const hasOutcome = Boolean(summary?.external_renovation_has_outcome);

  const hasFinalEpc = Boolean(summary?.external_renovation_has_final_epc);

  const projectStatus = String(
    summary?.external_renovation_project_status || "PLANNING",
  ).toUpperCase();

  /* =========================================================
     DATA
     ========================================================= */

  const externalOssName =
    summary?.external_oss_name ||
    t("externalRenovationCard.unknownOss", {
      defaultValue: "External OSS",
    });

  const countryCode = String(
    summary?.external_oss_country_code || "",
  ).toUpperCase();

  const countryName = COUNTRY_NAMES[countryCode] || countryCode || "—";

  const plannedCost = formatCurrency(
    summary?.external_renovation_planned_cost,
    countryCode,
  );

  const planFilename = summary?.external_renovation_plan_filename || "";

  const actualCost = formatCurrency(
    summary?.external_renovation_actual_cost,
    countryCode,
  );

  const actualCompletionDate = formatDate(
    summary?.external_renovation_actual_completion_date,
  );

  const finalEpcLabel = summary?.external_renovation_final_epc_label || "";

  const finalEpcScore =
    summary?.external_renovation_final_epc_energy_score ?? null;

  const finalEpcFilename =
    summary?.external_renovation_final_epc_filename || "";

  const dossierCode =
    summary?.external_renovation_dossier_code || summary?.dossier_code || "";

  const lastUpdated = formatDate(summary?.external_renovation_updated_at);

  /* =========================================================
     DISPLAY STATE
     ========================================================= */

  let tone = "active";

  let statusLabel = t("externalRenovationCard.status.linked", {
    defaultValue: "Linked to dossier",
  });

  let description = t("externalRenovationCard.description.linked", {
    defaultValue:
      "Your external OSS information and renovation plan are connected to your dossier and available for the renovation financing flow.",
  });

  let buttonLabel = t("externalRenovationCard.actions.manage", {
    defaultValue: "Manage details",
  });

  let buttonAction = onManage;

  /* ---------------------------------------------------------
     1. NO DOSSIER YET
     --------------------------------------------------------- */

  if (!isLinked && !hasDossier) {
    tone = "warning";

    statusLabel = t("externalRenovationCard.status.dossierRequired", {
      defaultValue: "Dossier required",
    });

    description = t("externalRenovationCard.description.dossierRequired", {
      defaultValue:
        "Your external renovation information has been saved. Create your financing dossier to continue and connect the renovation information to your financing process.",
    });

    buttonLabel = t("externalRenovationCard.actions.createDossier", {
      defaultValue: "Create financing dossier",
    });

    buttonAction = onCreateDossier;
  } else if (!isLinked && hasDossier) {
    /* ---------------------------------------------------------
     2. DOSSIER EXISTS, BUT NOT LINKED
     --------------------------------------------------------- */
    tone = "warning";

    statusLabel = t("externalRenovationCard.status.notLinked", {
      defaultValue: "Not linked",
    });

    description = t("externalRenovationCard.description.notLinked", {
      defaultValue:
        "Your external renovation information is ready. Link it to your financing dossier so it can be used by the lender during the renovation financing process.",
    });

    buttonLabel = t("externalRenovationCard.actions.linkDossier", {
      defaultValue: "Link to dossier",
    });

    buttonAction = onLinkDossier;
  } else if (isLinked && !hasPlan) {
    /* ---------------------------------------------------------
     3. LINKED, BUT RENOVATION PLAN MISSING
     --------------------------------------------------------- */
    tone = "pending";

    statusLabel = t("externalRenovationCard.status.planRequired", {
      defaultValue: "Plan required",
    });

    description = t("externalRenovationCard.description.planRequired", {
      defaultValue:
        "Your external renovation is linked to your dossier. Upload the renovation plan so the lender can review the renovation scope and planned cost.",
    });

    buttonLabel = t("externalRenovationCard.actions.uploadPlan", {
      defaultValue: "Upload renovation plan",
    });

    buttonAction = onManage;
  } else if (
    isLinked &&
    hasPlan &&
    projectStatus === "COMPLETED" &&
    !hasFinalEpc
  ) {
    /* ---------------------------------------------------------
     4. COMPLETED, FINAL EPC STILL MISSING
     --------------------------------------------------------- */
    tone = "pending";

    statusLabel = t("externalRenovationCard.status.finalEpcRequired", {
      defaultValue: "Final EPC pending",
    });

    description = t("externalRenovationCard.description.finalEpcRequired", {
      defaultValue:
        "The renovation has been recorded as completed. Add the final EPC certificate and outcome information so the delivered renovation results can be followed by the lender.",
    });

    buttonLabel = t("externalRenovationCard.actions.addOutcome", {
      defaultValue: "Complete project outcome",
    });

    buttonAction = onManage;
  } else if (
    isLinked &&
    hasPlan &&
    projectStatus === "COMPLETED" &&
    hasFinalEpc
  ) {
    /* ---------------------------------------------------------
     5. COMPLETED WITH FINAL EPC
     --------------------------------------------------------- */
    tone = "active";

    statusLabel = t("externalRenovationCard.status.completed", {
      defaultValue: "Renovation completed",
    });

    description = t("externalRenovationCard.description.completed", {
      defaultValue:
        "The renovation outcome and final EPC have been recorded. The financing record now includes the delivered project results as well as the original renovation plan.",
    });

    buttonLabel = t("externalRenovationCard.actions.reviewOutcome", {
      defaultValue: "Review project outcome",
    });

    buttonAction = onManage;
  } else if (isLinked && hasPlan && hasOutcome) {
    /* ---------------------------------------------------------
     6. OUTCOME DATA HAS STARTED
     --------------------------------------------------------- */
    tone = "active";

    statusLabel = t("externalRenovationCard.status.inProgress", {
      defaultValue: "Outcome in progress",
    });

    description = t("externalRenovationCard.description.inProgress", {
      defaultValue:
        "Outcome information has been added for this renovation. Keep the actual project details and final EPC up to date as the renovation progresses.",
    });

    buttonLabel = t("externalRenovationCard.actions.updateOutcome", {
      defaultValue: "Update project outcome",
    });

    buttonAction = onManage;
  }

  async function handleDisconnectExternalOss() {
    const renovationId = summary?.external_renovation_id;

    if (!renovationId || disconnecting) {
      return;
    }

    try {
      setDisconnecting(true);

      const accessToken = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/homeowner/external-renovations/${renovationId}/disconnect/`,
        {
          method: "POST",

          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.detail ||
            t("externalRenovationCard.disconnect.errorMessage", {
              defaultValue: "The external One-Stop Shop could not be removed.",
            }),
        );
      }

      setDisconnectModalOpen(false);

      showToast({
        type: "success",

        title: t("externalRenovationCard.disconnect.successTitle", {
          defaultValue: "External One-Stop Shop removed",
        }),

        message:
          payload?.detail ||
          t("externalRenovationCard.disconnect.successMessage", {
            defaultValue:
              "Your previous renovation information has been preserved.",
          }),
      });

      await onRefresh?.();
    } catch (error) {
      console.error("Could not disconnect external OSS:", error);

      showToast({
        type: "error",

        title: t("externalRenovationCard.disconnect.errorTitle", {
          defaultValue: "Could not remove external One-Stop Shop",
        }),

        message:
          error?.message ||
          t("externalRenovationCard.disconnect.errorMessage", {
            defaultValue: "The external One-Stop Shop could not be removed.",
          }),
      });
    } finally {
      setDisconnecting(false);
    }
  }

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <>
      <section
        className={`hb-external-renovation-card hb-external-renovation-card--${tone}`}
        aria-label={t("externalRenovationCard.ariaLabel", {
          defaultValue: "External renovation information",
        })}
      >
        <div className="hb-external-renovation-card__header">
          <div className="hb-external-renovation-card__heading">
            <span className="hb-external-renovation-card__eyebrow">
              {t("externalRenovationCard.eyebrow", {
                defaultValue: "External OSS",
              })}
            </span>

            <h2 className="hb-external-renovation-card__title">
              {t("externalRenovationCard.title", {
                defaultValue: "External renovation",
              })}
            </h2>
          </div>

          <span
            className={`hb-external-renovation-card__status hb-external-renovation-card__status--${tone}`}
          >
            {statusLabel}
          </span>
        </div>

        <p className="hb-external-renovation-card__description">
          {description}
        </p>

        <div className="hb-external-renovation-card__stats">
          <div className="hb-external-renovation-card__stat">
            <span className="hb-external-renovation-card__stat-label">
              {t("externalRenovationCard.fields.oss", {
                defaultValue: "External OSS",
              })}
            </span>

            <strong className="hb-external-renovation-card__stat-value">
              {externalOssName}
            </strong>

            <span className="hb-external-renovation-card__stat-helper">
              {t("externalRenovationCard.fields.ossHelper", {
                defaultValue: "Outside the LEG-UP network",
              })}
            </span>
          </div>

          <div className="hb-external-renovation-card__stat">
            <span className="hb-external-renovation-card__stat-label">
              {t("externalRenovationCard.fields.country", {
                defaultValue: "Country",
              })}
            </span>

            <strong className="hb-external-renovation-card__stat-value">
              {countryName}
            </strong>

            {countryCode && (
              <span className="hb-external-renovation-card__stat-helper">
                {countryCode}
              </span>
            )}
          </div>

          <div className="hb-external-renovation-card__stat">
            <span className="hb-external-renovation-card__stat-label">
              {t("externalRenovationCard.fields.plannedCost", {
                defaultValue: "Planned renovation cost",
              })}
            </span>

            <strong className="hb-external-renovation-card__stat-value">
              {plannedCost}
            </strong>

            <span className="hb-external-renovation-card__stat-helper">
              {t("externalRenovationCard.fields.plannedCostHelper", {
                defaultValue: "Provided for the external renovation",
              })}
            </span>
          </div>

          <div className="hb-external-renovation-card__stat">
            <span className="hb-external-renovation-card__stat-label">
              {t("externalRenovationCard.fields.plan", {
                defaultValue: "Renovation plan",
              })}
            </span>

            <strong className="hb-external-renovation-card__stat-value">
              {hasPlan
                ? t("externalRenovationCard.fields.planUploaded", {
                    defaultValue: "Uploaded",
                  })
                : t("externalRenovationCard.fields.planMissing", {
                    defaultValue: "Not uploaded",
                  })}
            </strong>

            <span
              className="hb-external-renovation-card__stat-helper hb-external-renovation-card__stat-helper--filename"
              title={planFilename || undefined}
            >
              {planFilename ||
                t("externalRenovationCard.fields.planHelper", {
                  defaultValue: "Add the renovation plan when available",
                })}
            </span>
          </div>

          {hasOutcome && (
            <div className="hb-external-renovation-card__stat">
              <span className="hb-external-renovation-card__stat-label">
                {t("externalRenovationCard.fields.actualCost", {
                  defaultValue: "Actual renovation cost",
                })}
              </span>

              <strong className="hb-external-renovation-card__stat-value">
                {actualCost}
              </strong>

              <span className="hb-external-renovation-card__stat-helper">
                {summary?.external_renovation_actual_completion_date
                  ? t("externalRenovationCard.fields.completedOn", {
                      defaultValue: "Completed {{date}}",
                      date: actualCompletionDate,
                    })
                  : t("externalRenovationCard.fields.actualCostHelper", {
                      defaultValue: "Latest recorded project cost",
                    })}
              </span>
            </div>
          )}

          {(hasFinalEpc || finalEpcLabel || finalEpcScore !== null) && (
            <div className="hb-external-renovation-card__stat">
              <span className="hb-external-renovation-card__stat-label">
                {t("externalRenovationCard.fields.finalEpc", {
                  defaultValue: "Final EPC",
                })}
              </span>

              <strong className="hb-external-renovation-card__stat-value">
                {finalEpcLabel ||
                  t("externalRenovationCard.fields.finalEpcUploaded", {
                    defaultValue: "Uploaded",
                  })}
              </strong>

              <span
                className="hb-external-renovation-card__stat-helper hb-external-renovation-card__stat-helper--filename"
                title={finalEpcFilename || undefined}
              >
                {finalEpcScore !== null && finalEpcScore !== ""
                  ? `${finalEpcScore} kWh/m²/year`
                  : finalEpcFilename ||
                    t("externalRenovationCard.fields.finalEpcHelper", {
                      defaultValue: "Final energy performance recorded",
                    })}
              </span>
            </div>
          )}
        </div>

        <div className="hb-external-renovation-card__link-summary">
          <div className="hb-external-renovation-card__link-summary-main">
            <span className="hb-external-renovation-card__link-label">
              {t("externalRenovationCard.fields.dossierLink", {
                defaultValue: "Dossier connection",
              })}
            </span>

            <strong>
              {isLinked
                ? dossierCode ||
                  t("externalRenovationCard.fields.linked", {
                    defaultValue: "Linked",
                  })
                : !hasDossier
                  ? t("externalRenovationCard.fields.noDossier", {
                      defaultValue: "No dossier yet",
                    })
                  : t("externalRenovationCard.fields.notLinked", {
                      defaultValue: "Not linked yet",
                    })}
            </strong>
          </div>

          <div className="hb-external-renovation-card__updated">
            <span>
              {t("externalRenovationCard.fields.lastUpdated", {
                defaultValue: "Last updated",
              })}
            </span>

            <strong>{lastUpdated}</strong>
          </div>
        </div>

        <div className="hb-external-renovation-card__actions">
          <button
            type="button"
            className="hb-external-renovation-card__button"
            onClick={buttonAction}
            disabled={!buttonAction || disconnecting}
          >
            {buttonLabel}

            <span aria-hidden="true">→</span>
          </button>

          <button
            type="button"
            className={
              "hb-external-renovation-card__button " +
              "hb-external-renovation-card__button--danger"
            }
            onClick={() => setDisconnectModalOpen(true)}
            disabled={disconnecting || !summary?.external_renovation_id}
          >
            <Unplug size={15} />

            {t("externalRenovationCard.disconnect.button", {
              defaultValue: "Stop using external OSS",
            })}
          </button>
        </div>
      </section>
    </>
  );
}
